/**
 * Modelo musical do curso.
 *
 * Notação internacional usada internamente (C4, D4, ...), mas o aluno sempre
 * vê os nomes em português (Dó, Ré, Mi, Fá, Sol, Lá, Si).
 *
 * Importante: cada nota carrega a sua altura E a sua oitava (via `midi`),
 * então Dó4 nunca é confundido com outro Dó de oitava diferente.
 */

export type Accidental = -1 | 0 | 1;

export interface NoteDef {
  /** id internacional, ex: "C4" */
  id: string;
  letter: string;
  octave: number;
  /** número MIDI (C4 = 60, o Dó central) */
  midi: number;
  /** posição no pentagrama em "espaços de pauta", linha de Sol = 0 */
  step: number;
  accidental: Accidental;
  /** nome em português, ex: "Dó" */
  ptName: string;
  /** nome em português com oitava, ex: "Dó4" */
  label: string;
}

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
export type Letter = (typeof LETTERS)[number];

/** semitons a partir de Dó dentro da oitava */
const SEMITONE: Record<Letter, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

const PT_NAME: Record<Letter, string> = {
  C: 'Dó',
  D: 'Ré',
  E: 'Mi',
  F: 'Fá',
  G: 'Sol',
  A: 'Lá',
  B: 'Si',
};

/**
 * Distância em posições de pauta (metade de um espaço por grau) em relação
 * à linha de Sol (G4), que é a 2ª linha da pauta na clave de sol.
 * C4 fica 2 espaços abaixo (linha suplementar inferior).
 */
function stepFromLetterOctave(letter: Letter, octave: number): number {
  const letterIndex = LETTERS.indexOf(letter);
  const diatonicFromC4 = (octave - 4) * 7 + letterIndex;
  return (diatonicFromC4 - 4) * 0.5;
}

function buildNote(letter: Letter, octave: number): NoteDef {
  const id = `${letter}${octave}`;
  const midi = (octave + 1) * 12 + SEMITONE[letter];
  const ptName = PT_NAME[letter];
  return {
    id,
    letter,
    octave,
    midi,
    step: stepFromLetterOctave(letter, octave),
    accidental: 0,
    ptName,
    label: `${ptName}${octave}`,
  };
}

function buildNaturals(fromOctave: number, toOctave: number): NoteDef[] {
  const list: NoteDef[] = [];
  for (let octave = fromOctave; octave <= toOctave; octave += 1) {
    LETTERS.forEach((letter) => {
      list.push(buildNote(letter, octave));
    });
  }
  return list;
}

/** Todas as notas naturais de C3 a C6 (suficiente para o teclado do curso). */
export const NATURAL_NOTES: NoteDef[] = buildNaturals(3, 6);

export const NOTES_BY_ID: Record<string, NoteDef> = NATURAL_NOTES.reduce(
  (acc, note) => {
    acc[note.id] = note;
    return acc;
  },
  {} as Record<string, NoteDef>,
);

export function getNote(id: string): NoteDef {
  const note = NOTES_BY_ID[id];
  if (!note) {
    throw new Error(`Nota desconhecida: ${id}`);
  }
  return note;
}

export function getNoteSafe(id: string): NoteDef | null {
  return NOTES_BY_ID[id] ?? null;
}

/** Ids das sete notas naturais da região inicial do curso (Dó4 a Si4). */
export const FIRST_REGION_IDS = ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4'];

/** Dó central = C4. */
export const MIDDLE_C_ID = 'C4';

export function midiToFrequency(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

const LETTER_BY_SEMITONE: Record<number, { letter: Letter; sharp: boolean }> = {
  0: { letter: 'C', sharp: false },
  1: { letter: 'C', sharp: true },
  2: { letter: 'D', sharp: false },
  3: { letter: 'D', sharp: true },
  4: { letter: 'E', sharp: false },
  5: { letter: 'F', sharp: false },
  6: { letter: 'F', sharp: true },
  7: { letter: 'G', sharp: false },
  8: { letter: 'G', sharp: true },
  9: { letter: 'A', sharp: false },
  10: { letter: 'A', sharp: true },
  11: { letter: 'B', sharp: false },
};

export interface KeyboardKeyInfo {
  midi: number;
  isBlack: boolean;
  /** nome exibido nas teclas, ex: "Dó4" ou "Dó#4" */
  label: string;
  octave: number;
}

export function describeMidi(midi: number): KeyboardKeyInfo {
  const semitone = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const info = LETTER_BY_SEMITONE[semitone];
  const name = PT_NAME[info.letter];
  const label = info.sharp ? `${name}#${octave}` : `${name}${octave}`;
  return { midi, isBlack: info.sharp, label, octave };
}